import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { VolumeX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  ALERT_SOUND_BUTTON_ATTR, isAlertSoundReady, isAlertSoundSupported, playAlertSound, subscribeAlertSound,
  unlockAlertSound,
} from '@/lib/alertSound'
import { useElementBottomVar } from '@/hooks/useElementHeightVar'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'

/** #337 (fix round 1) — a press that unlocked the sound but ends in no click (a drag) keeps the bar this long at most */
const PRESS_HOLD_MAX_MS = 1_000

/**
 * #337 — after a reload the browser keeps the alert sounds blocked until the user clicks the page; the small header
 * button "Sound off" (#295) was the only sign. While blocked, this full-width amber bar under the header says so.
 * The whole bar is a button that does what the header button does: it carries the same attribute, so the page-wide
 * unlock plays the test chime once when the gesture starts on it, and its click is the fallback (#295). It goes away
 * once the sound runs, whatever click unlocked it (it follows the sound state). Same condition as the header
 * button: not shown where Web Audio is missing (a click could not help). No animation (reduced motion is safe).
 *
 * Fix round 1 (review):
 * - I-1: the unlock runs on the press (pointerdown); a bar that left at once moved the page under the pointer before
 *   the click, which then missed its target. While a pointer is pressed the bar stays until that gesture's click
 *   (PRESS_HOLD_MAX_MS at most); with no press (the keyboard, the header button's own unlock path, the browser
 *   allowing sound) it goes at once.
 * - I-5: while it shows, its bottom edge is published as --sound-bar-bottom; the toasts and the critical popups start
 *   below it when no critical bar shows (index.css).
 * - M-6: it waits one tick before it first shows: the sound module is installed by WebSocketProvider's mount effect,
 *   which runs after this component's, so where the browser already allows sound the bar never flashes on load.
 */
export function SoundBar() {
  const { t } = useTranslation('notifications')
  const ready = useSyncExternalStore(subscribeAlertSound, isAlertSoundReady, () => false)
  const ref = useRef<HTMLDivElement>(null)
  // #393 — phones (under 640 px): a shorter sentence at 13 px so the bar is one line down to 320 px
  const phone = !useMediaQuery('(min-width: 640px)', true)

  const [settled, setSettled] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), 0)
    return () => clearTimeout(timer)
  }, [])

  // Is a pointer pressed right now? (capture, like the unlock listener; reset once that gesture's click is over)
  const pressed = useRef(false)
  useEffect(() => {
    const down = () => { pressed.current = true }
    const done = () => { setTimeout(() => { pressed.current = false }, 0) }
    window.addEventListener('pointerdown', down, true)
    window.addEventListener('click', done, true)
    return () => {
      window.removeEventListener('pointerdown', down, true)
      window.removeEventListener('click', done, true)
    }
  }, [])

  const [gone, setGone] = useState(ready)
  useEffect(() => {
    if (!ready) { setGone(false); return }
    if (!pressed.current) { setGone(true); return }
    // the click's target is decided before it is dispatched: the bar may go as the click starts
    const hide = () => setGone(true)
    const timer = setTimeout(hide, PRESS_HOLD_MAX_MS)
    window.addEventListener('click', hide, { capture: true, once: true })
    return () => {
      clearTimeout(timer)
      window.removeEventListener('click', hide, { capture: true })
    }
  }, [ready])

  const shown = settled && !gone && isAlertSoundSupported()
  useElementBottomVar(ref, '--sound-bar-bottom', shown)
  if (!shown) return null
  return (
    <div
      ref={ref}
      role="status"
      data-sound-bar=""
      className="border-b border-amber-500/30 bg-amber-500/15 text-amber-900 dark:text-amber-200"
    >
      <button
        type="button"
        {...{ [ALERT_SOUND_BUTTON_ATTR]: '' }}
        onClick={() => { void unlockAlertSound().then((on) => { if (on) playAlertSound() }) }}
        className={cn(
          'flex w-full items-center justify-center gap-2 px-3 py-2 text-center font-medium transition-colors hover:bg-amber-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500',
          phone ? 'text-[13px] whitespace-nowrap' : 'text-sm',
        )}
      >
        <VolumeX className="h-4 w-4 flex-shrink-0" aria-hidden />
        <span>{t(phone ? 'soundBar.textShort' : 'soundBar.text')}</span>
      </button>
    </div>
  )
}
